import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBell, 
  FaTimes, 
  FaCalendarCheck, 
  FaCalendarDay,
  FaInfoCircle,
  FaCheckCircle,
  FaTools,
  FaExclamationTriangle,
  FaShieldAlt,
  FaCarCrash,
  FaTachometerAlt,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaAngleDown,
  FaAngleUp
} from 'react-icons/fa';
import { TbGps } from 'react-icons/tb';
import axios from 'axios';
import moment from 'moment';
import EditRentalModal from '../RentalModal/EditRentalModal';
import VehicleDetailsModal from '../VehicleManagement/VehicleDetailsModal';

const UnifiedNotificationCenter = ({ rentals, onRentalUpdate, token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [maintenanceVehicles, setMaintenanceVehicles] = useState([]);
  const [expiringVehicles, setExpiringVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState({
    urgent: true,
    maintenance: false,
    insurance: false,
    upcoming: false
  });

  // Modal states
  const [selectedRental, setSelectedRental] = useState(null);
  const [isRentalModalOpen, setIsRentalModalOpen] = useState(false);
  const [isUpdatingRental, setIsUpdatingRental] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);

  // Load dismissed notifications from localStorage on component mount
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedNotifications');
    if (dismissed) {
      setDismissedNotifications(new Set(JSON.parse(dismissed)));
    }
  }, []);
  // Fetch notification counts on component mount and when dependencies change
  useEffect(() => {
    fetchNotificationCounts();
  }, [rentals, token, dismissedNotifications]);

  // Fetch all notifications when modal is opened
  useEffect(() => {
    if (isOpen) {
      fetchAllNotifications();
    }
  }, [isOpen]);

  const fetchNotificationCounts = async () => {
    try {
      // Generate rental notifications count
      const rentalNotifications = generateRentalNotifications();
      
      // Fetch maintenance and insurance counts in parallel
      const [maintenanceResponse, insuranceResponse] = await Promise.all([
        axios.get('https://rent-server.create-site.gr/api/cars/maintenance/upcoming', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get('https://rent-server.create-site.gr/api/cars/insurance/expiring', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      const maintenanceData = maintenanceResponse.data || [];
      const insuranceData = insuranceResponse.data || [];

      // Filter out dismissed notifications when counting
      const activeRentalNotifications = rentalNotifications.filter(n => !isNotificationDismissed(n.id));
      const activeMaintenanceData = maintenanceData.filter(v => !isNotificationDismissed(`maintenance-${v._id}`));
      const activeInsuranceData = insuranceData.filter(v => !isNotificationDismissed(`insurance-${v._id}`));

      // Calculate counts
      const urgentRentals = activeRentalNotifications.filter(n => n.category === 'urgent').length;
      const criticalMaintenance = activeMaintenanceData.filter(v => ['critical', 'high'].includes(getMaintenanceUrgency(v))).length;
      const expiredInsurance = activeInsuranceData.filter(v => getInsuranceUrgency(v) === 'expired').length;
      const expiringSoonInsurance = activeInsuranceData.filter(v => ['critical', 'high'].includes(getInsuranceUrgency(v))).length;

      const totalUrgent = urgentRentals + criticalMaintenance + expiredInsurance + expiringSoonInsurance;
      const totalAll = activeRentalNotifications.length + activeMaintenanceData.length + activeInsuranceData.length;

      setUrgentCount(totalUrgent);
      setTotalCount(totalAll);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
      setUrgentCount(0);
      setTotalCount(0);
    }
  };

  const fetchAllNotifications = async () => {
    setLoading(true);
    try {
      await Promise.all([
        generateRentalNotifications(),
        fetchMaintenanceData(),
        fetchInsuranceData()
      ]);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRentalNotifications = () => {
    if (!rentals || rentals.length === 0) {
      setNotifications([]);
      return [];
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    const tomorrowDate = new Date(currentDate);
    tomorrowDate.setDate(currentDate.getDate() + 1);
    
    let newNotifications = [];
    
    rentals.forEach(rental => {
      const pickupDate = new Date(rental.pickupDateTime);
      pickupDate.setHours(0, 0, 0, 0);
      
      const returnDate = new Date(rental.returnDateTime);
      returnDate.setHours(0, 0, 0, 0);
      
      const customerName = `${rental.customer.firstName} ${rental.customer.lastName}`;
      const pickupTime = new Date(rental.pickupDateTime);
      const returnTime = new Date(rental.returnDateTime);
      
      if (pickupDate.getTime() === currentDate.getTime() && rental.status === 'pending') {
        newNotifications.push({
          id: `pickup-today-${rental._id}`,
          type: 'pickup-today',
          category: 'urgent',
          title: '🔴 URGENT: Car Pickup Today',
          message: `${rental.carName} is scheduled for pickup today at ${rental.pickupLocation}`,
          time: pickupTime,
          customer: customerName,
          rentalId: rental._id,
          rental: rental,
          targetStatus: 'confirmed'
        });
      }
      else if (pickupDate.getTime() === tomorrowDate.getTime() && rental.status === 'pending') {
        newNotifications.push({
          id: `pickup-tomorrow-${rental._id}`,
          type: 'pickup-tomorrow',
          category: 'upcoming',
          title: '⚠️ Car Pickup Tomorrow',
          message: `${rental.carName} is scheduled for pickup tomorrow at ${rental.pickupLocation}`,
          time: pickupTime,
          customer: customerName,
          rentalId: rental._id,
          rental: rental
        });
      }
      
      if (returnDate.getTime() === currentDate.getTime() && rental.status === 'confirmed') {
        newNotifications.push({
          id: `return-today-${rental._id}`,
          type: 'return-today',
          category: 'urgent',
          title: '🔴 URGENT: Car Return Today',
          message: `${rental.carName} is scheduled for return today at ${rental.returnLocation}`,
          time: returnTime,
          customer: customerName,
          rentalId: rental._id,
          rental: rental,
          targetStatus: 'completed'
        });
      }
      else if (returnDate.getTime() === tomorrowDate.getTime() && rental.status === 'confirmed') {
        newNotifications.push({
          id: `return-tomorrow-${rental._id}`,
          type: 'return-tomorrow',
          category: 'upcoming',
          title: '⚠️ Car Return Tomorrow',
          message: `${rental.carName} is scheduled for return tomorrow at ${rental.returnLocation}`,
          time: returnTime,
          customer: customerName,
          rentalId: rental._id,
          rental: rental
        });
      }
    });
    
    setNotifications(newNotifications);
    return newNotifications;
  };

  const fetchMaintenanceData = async () => {
    try {
      const response = await axios.get('https://rent-server.create-site.gr/api/cars/maintenance/upcoming', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMaintenanceVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
      setMaintenanceVehicles([]);
    }
  };

  const fetchInsuranceData = async () => {
    try {
      const response = await axios.get('https://rent-server.create-site.gr/api/cars/insurance/expiring', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExpiringVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance data:', error);
      setExpiringVehicles([]);
    }
  };

  // Helper functions for maintenance notifications
  const getMaintenanceUrgency = (vehicle) => {
    if (!vehicle.lastServiceDate) return 'critical';
    const daysSinceService = moment().diff(moment(vehicle.lastServiceDate), 'days');
    if (daysSinceService > 300) return 'critical';
    if (daysSinceService > 180) return 'high';
    if (daysSinceService > 150) return 'medium';
    return 'low';
  };

  // Helper functions for insurance notifications
  const getDaysUntilExpiration = (endDate) => {
    const now = moment();
    const expiry = moment(endDate);
    return expiry.diff(now, 'days');
  };

  const getInsuranceUrgency = (vehicle) => {
    const daysLeft = getDaysUntilExpiration(vehicle.insurance.endDate);
    if (daysLeft < 0) return 'expired';
    if (daysLeft <= 7) return 'critical';
    if (daysLeft <= 14) return 'high';
    if (daysLeft <= 30) return 'medium';
    return 'low';
  };

  // Helper functions for dismissed notifications
  const generateNotificationId = (notification, type) => {
    if (type === 'rental') {
      return notification.id;
    } else if (type === 'maintenance') {
      return `maintenance-${notification._id}`;
    } else if (type === 'insurance') {
      return `insurance-${notification._id}`;
    }
    return `${type}-${notification._id || Math.random()}`;
  };  const dismissNotification = (notificationId) => {
    const newDismissed = new Set(dismissedNotifications);
    newDismissed.add(notificationId);
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify([...newDismissed]));
  };
  const dismissAllNotifications = () => {
    const allNotificationIds = [
      ...notifications.map(n => n.id),
      ...maintenanceVehicles.map(v => `maintenance-${v._id}`),
      ...expiringVehicles.map(v => `insurance-${v._id}`)
    ];
    
    const newDismissed = new Set([...dismissedNotifications, ...allNotificationIds]);
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify([...newDismissed]));
  };

  const dismissSectionNotifications = (sectionType) => {
    let sectionNotificationIds = [];
    
    switch (sectionType) {
      case 'urgent':
        sectionNotificationIds = urgentNotifications.map(n => n.id);
        break;
      case 'upcoming':
        sectionNotificationIds = upcomingNotifications.map(n => n.id);
        break;
      case 'maintenance':
        sectionNotificationIds = maintenanceVehicles.map(v => `maintenance-${v._id}`);
        break;
      case 'insurance':
        sectionNotificationIds = expiringVehicles.map(v => `insurance-${v._id}`);
        break;
      default:
        return;
    }
      const newDismissed = new Set([...dismissedNotifications, ...sectionNotificationIds]);
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify([...newDismissed]));
  };

  const isNotificationDismissed = (notificationId) => {
    return dismissedNotifications.has(notificationId);
  };

  // Filter and categorize notifications (excluding dismissed ones)
  const urgentNotifications = notifications.filter(n => n.category === 'urgent' && !isNotificationDismissed(n.id));
  const upcomingNotifications = notifications.filter(n => n.category === 'upcoming' && !isNotificationDismissed(n.id));
  const criticalMaintenanceVehicles = maintenanceVehicles.filter(v => 
    ['critical', 'high'].includes(getMaintenanceUrgency(v)) && !isNotificationDismissed(`maintenance-${v._id}`)
  );
  const expiredInsuranceVehicles = expiringVehicles.filter(v => 
    getInsuranceUrgency(v) === 'expired' && !isNotificationDismissed(`insurance-${v._id}`)
  );
  const expiringSoonInsuranceVehicles = expiringVehicles.filter(v => 
    ['critical', 'high'].includes(getInsuranceUrgency(v)) && !isNotificationDismissed(`insurance-${v._id}`)
  );

  // Click handlers
  const handleRentalClick = (notification) => {
    setSelectedRental(notification.rental);
    setIsRentalModalOpen(true);
    setIsOpen(false);
  };

  const handleVehicleClick = async (vehicle, type) => {
    try {
      const carId = vehicle.carId || vehicle.carModelId;
      
      if (carId) {
        const response = await axios.get(`https://rent-server.create-site.gr/api/cars/${carId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data) {
          const vehicleInstance = response.data.vehicleInstances?.find(v => v._id === vehicle._id);
          
          const enhancedVehicle = vehicleInstance ? {
            ...vehicleInstance,
            carId: response.data._id,
            car: response.data
          } : {
            ...vehicle,
            carId: response.data._id,
            carModelId: response.data._id,
            carMake: response.data.make,
            carModel: response.data.model,
            car: response.data
          };
          
          setSelectedVehicle(enhancedVehicle);
          setSelectedCar(response.data);
        } else {
          setSelectedVehicle({
            ...vehicle,
            carId: carId,
            carModelId: carId
          });
          setSelectedCar({
            _id: carId,
            make: vehicle.carMake || vehicle.make || 'Unknown Make',
            model: vehicle.carModel || vehicle.model || 'Unknown Model',
            year: vehicle.year || new Date().getFullYear()
          });
        }
      } else {
        const reconstructedCarId = `car_${vehicle._id}`;
        setSelectedVehicle({
          ...vehicle,
          carId: reconstructedCarId,
          carModelId: reconstructedCarId,
          carMake: vehicle.carMake || vehicle.make || 'Unknown Make',
          carModel: vehicle.carModel || vehicle.model || 'Unknown Model'
        });
        setSelectedCar({
          _id: reconstructedCarId,
          make: vehicle.carMake || vehicle.make || 'Unknown Make',
          model: vehicle.carModel || vehicle.model || 'Unknown Model',
          year: vehicle.year || new Date().getFullYear()
        });
      }
      
      setIsVehicleModalOpen(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Error preparing vehicle data:', error);
      setSelectedVehicle({
        ...vehicle,
        carId: vehicle.carId || `car_${vehicle._id}`,
        carModelId: vehicle.carModelId || `car_${vehicle._id}`
      });
      setSelectedCar({
        _id: vehicle.carId || `car_${vehicle._id}`,
        make: vehicle.carMake || vehicle.make || 'Unknown Make',
        model: vehicle.carModel || vehicle.model || 'Unknown Model',
        year: vehicle.year || new Date().getFullYear()
      });
      setIsVehicleModalOpen(true);
      setIsOpen(false);
    }
  };

  const handleNotificationAction = (notification) => {
    if (notification.targetStatus && notification.rentalId) {
      onRentalUpdate(notification.rentalId, notification.targetStatus);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleRentalUpdate = async (updateData) => {
    setIsUpdatingRental(true);
    try {
      await onRentalUpdate(selectedRental._id, updateData);
      setIsRentalModalOpen(false);
      setSelectedRental(null);
      // Refresh notifications after update
      fetchNotificationCounts();
      if (isOpen) {
        fetchAllNotifications();
      }
    } catch (error) {
      console.error('Error updating rental:', error);
    } finally {
      setIsUpdatingRental(false);
    }
  };
  const NotificationSection = ({ title, items, type, expanded, onToggle, icon, sectionKey }) => (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          {icon}
          <span className="font-medium text-sm ml-2">{title}</span>
          <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
            {items.length}
          </span>
        </div>
        <div className="flex items-center">
          {items.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissSectionNotifications(sectionKey);
              }}
              className="mr-2 text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1 hover:bg-red-50 rounded"
              title={`Dismiss all ${title.toLowerCase()}`}
            >
              Dismiss All
            </button>
          )}
          {expanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </div>
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">
              {items.map((item, index) => (
                <div key={item.id || index} className="mb-2 last:mb-0">
                  {type === 'rental' && (
                    <RentalNotificationItem 
                      notification={item} 
                      onClick={() => handleRentalClick(item)}
                      onAction={() => handleNotificationAction(item)}
                    />
                  )}
                  {type === 'maintenance' && (
                    <MaintenanceNotificationItem 
                      vehicle={item} 
                      onClick={() => handleVehicleClick(item, 'maintenance')}
                    />
                  )}
                  {type === 'insurance' && (
                    <InsuranceNotificationItem 
                      vehicle={item} 
                      onClick={() => handleVehicleClick(item, 'insurance')}
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const RentalNotificationItem = ({ notification, onClick, onAction }) => {
    const getBgColor = () => {
      switch (notification.type) {
        case 'pickup-today':
        case 'return-today':
          return 'bg-red-50 border-red-200 hover:bg-red-100';
        case 'pickup-tomorrow':
        case 'return-tomorrow':
          return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
        default:
          return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      }
    };

    const getIcon = () => {
      switch (notification.type) {
        case 'pickup-today':
        case 'pickup-tomorrow':
          return <FaCalendarDay className="text-blue-600" />;
        case 'return-today':
        case 'return-tomorrow':
          return <FaCalendarCheck className="text-green-600" />;
        default:
          return <FaInfoCircle className="text-blue-500" />;
      }
    };

    return (
      <div className={`p-3 rounded-lg border transition-colors relative ${getBgColor()}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissNotification(notification.id);
          }}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <FaTimes size={12} />
        </button>
        <div 
          className="cursor-pointer pr-6"
          onClick={onClick}
        >
          <div className="flex items-start">
            <div className="mr-3 mt-0.5">{getIcon()}</div>
            <div className="flex-1">
              <div className="font-medium text-sm">{notification.title}</div>
              <div className="text-xs text-gray-600 mt-1">{notification.message}</div>
              {notification.time && (
                <div className="text-xs font-medium mt-1">
                  Time: {notification.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {notification.customer && (
                <div className="text-xs font-medium mt-1">Customer: {notification.customer}</div>
              )}
            </div>
          </div>
          {notification.targetStatus && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAction();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs py-1 px-3 rounded transition-colors"
              >
                <FaCheckCircle className="inline mr-1" />
                Mark as {notification.targetStatus}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const MaintenanceNotificationItem = ({ vehicle, onClick }) => {
    const urgency = getMaintenanceUrgency(vehicle);
    const getColorClass = () => {
      switch (urgency) {
        case 'critical': return 'bg-red-50 border-red-200 hover:bg-red-100';
        case 'high': return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
        case 'medium': return 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
        default: return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      }
    };

    return (
      <div className={`p-3 rounded-lg border transition-colors relative ${getColorClass()}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissNotification(`maintenance-${vehicle._id}`);
          }}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <FaTimes size={12} />
        </button>
        <div 
          className="cursor-pointer pr-6"
          onClick={onClick}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaTools className="text-gray-600 mr-2" />
              <div>
                <div className="font-medium text-sm">{vehicle.carMake} {vehicle.carModel}</div>
                <div className="text-xs text-gray-600">{vehicle.licensePlate}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-medium capitalize">{urgency}</div>
              {vehicle.lastServiceDate ? (
                <div className="text-xs text-gray-600">
                  Last: {moment(vehicle.lastServiceDate).format('DD/MM/YY')}
                </div>
              ) : (
                <div className="text-xs text-red-600">No history</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const InsuranceNotificationItem = ({ vehicle, onClick }) => {
    const urgency = getInsuranceUrgency(vehicle);
    const daysLeft = getDaysUntilExpiration(vehicle.insurance.endDate);
    
    const getColorClass = () => {
      switch (urgency) {
        case 'expired': return 'bg-red-50 border-red-200 hover:bg-red-100';
        case 'critical': return 'bg-red-50 border-red-200 hover:bg-red-100';
        case 'high': return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
        default: return 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
      }
    };

    const formatDaysLeft = () => {
      if (daysLeft < 0) return `Expired ${Math.abs(daysLeft)} days ago`;
      if (daysLeft === 0) return 'Expires today';
      if (daysLeft === 1) return 'Expires tomorrow';
      return `Expires in ${daysLeft} days`;
    };

    return (
      <div className={`p-3 rounded-lg border transition-colors relative ${getColorClass()}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissNotification(`insurance-${vehicle._id}`);
          }}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <FaTimes size={12} />
        </button>
        <div 
          className="cursor-pointer pr-6"
          onClick={onClick}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaShieldAlt className="text-gray-600 mr-2" />
              <div>
                <div className="font-medium text-sm">{vehicle.carMake} {vehicle.carModel}</div>
                <div className="text-xs text-gray-600">{vehicle.licensePlate}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-medium">{formatDaysLeft()}</div>
              <div className="text-xs text-gray-600">{vehicle.insurance.provider}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop for notification dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="relative">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <FaBell size={20} />
          {totalCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5.5 w-7 flex items-center justify-center">
            {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
        </button>
        
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-lg shadow-xl z-40 border border-gray-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()
              }
            >              <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0">
                <h3 className="font-medium text-gray-800">All Notifications</h3>
                <div className="flex items-center space-x-2">
                  {totalCount > 0 && (
                    <button
                      onClick={dismissAllNotifications}
                      className="text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1 hover:bg-red-50 rounded border border-gray-300 hover:border-red-300"
                      title="Dismiss all notifications"
                    >
                      Dismiss All
                    </button>
                  )}
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">Loading notifications...</div>
                ) : (
                  <>                    {urgentNotifications.length > 0 && (
                      <NotificationSection
                        title="🔴 Urgent Rentals"
                        items={urgentNotifications}
                        type="rental"
                        expanded={expandedSections.urgent}
                        onToggle={() => toggleSection('urgent')}
                        icon={<FaExclamationTriangle className="text-red-500" />}
                        sectionKey="urgent"
                      />
                    )}
                    
                    {(expiredInsuranceVehicles.length > 0 || expiringSoonInsuranceVehicles.length > 0) && (
                      <NotificationSection
                        title="🛡️ Insurance Alerts"
                        items={[...expiredInsuranceVehicles, ...expiringSoonInsuranceVehicles]}
                        type="insurance"
                        expanded={expandedSections.insurance}
                        onToggle={() => toggleSection('insurance')}
                        icon={<FaShieldAlt className="text-blue-500" />}
                        sectionKey="insurance"
                      />
                    )}
                    
                    {criticalMaintenanceVehicles.length > 0 && (
                      <NotificationSection
                        title="🔧 Maintenance Alerts"
                        items={criticalMaintenanceVehicles}
                        type="maintenance"
                        expanded={expandedSections.maintenance}
                        onToggle={() => toggleSection('maintenance')}
                        icon={<FaTools className="text-orange-500" />}
                        sectionKey="maintenance"
                      />
                    )}
                    
                    {upcomingNotifications.length > 0 && (
                      <NotificationSection
                        title="📅 Upcoming"
                        items={upcomingNotifications}
                        type="rental"
                        expanded={expandedSections.upcoming}
                        onToggle={() => toggleSection('upcoming')}
                        icon={<FaCalendarAlt className="text-yellow-500" />}
                        sectionKey="upcoming"
                      />
                    )}
                    
                    {totalCount === 0 && (
                      <div className="p-4 text-center text-gray-500">No notifications</div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Rental Edit Modal */}
      {selectedRental && (
        <EditRentalModal
          rental={selectedRental}
          isOpen={isRentalModalOpen}
          onClose={() => {
            setIsRentalModalOpen(false);
            setSelectedRental(null);
          }}
          onUpdate={handleRentalUpdate}
          isUpdating={isUpdatingRental}
        />
      )}

      {/* Vehicle Details Modal */}
      {selectedVehicle && selectedCar && (
        <VehicleDetailsModal
          vehicle={selectedVehicle}
          car={selectedCar}
          isOpen={isVehicleModalOpen}
          onClose={() => {
            setIsVehicleModalOpen(false);
            setSelectedVehicle(null);
            setSelectedCar(null);
          }}
          onUpdate={() => {
            // Refresh notifications after vehicle update
            fetchAllNotifications();
          }}
        />
      )}
    </>
  );
};

export default UnifiedNotificationCenter;
